import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import Container from '@magoosh/layouts/components/container';
import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import UnderlinedText from '@marketing/components/underlined_text';
import FAQSection from '@marketing/sections/faq';

import AISection from './sections/ai';
import AppsSection from './sections/apps';
import FeaturesSection from './sections/features';
import ValuePropsSection from './sections/value_props';
import VideoBasedLearningSection from './sections/video_based_learning';
import WallOfLoveSection from './sections/wall_of_love';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import GuaranteeSection from '../sections/guarantee';
import HeroSection from '../sections/hero';
import OfficialQuestionsSection from './sections/official_questions';
import SocialProofSection from '../sections/social_proof';

import marketingStyles from '@marketing/style.module.scss';
import styles from '../style.module.scss';
import toeflStyles from './style.module.scss';

interface Props extends PageProps {
  mapInCents: number;
}

export default (props: Props) => {
  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.purpleGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />

        <Container>
          <div style={{ position: 'absolute', left: 0, top: -25, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzOTAiIGhlaWdodD0iNDExIiBmaWxsPSJub25lIj48bWFzayBpZD0iYiIgd2lkdGg9IjQ2MCIgaGVpZ2h0PSI0NTYiIHg9Ii05OCIgeT0iLTE0NiIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNhKSIgZD0iTTIxNi41MTYtMTQ1LjExMmgzMzh2MzQ4aC0zMzh6IiB0cmFuc2Zvcm09InJvdGF0ZSg2NC42MTMgMjE2LjUxNiAtMTQ1LjExMikiLz48L21hc2s+PGcgc3Ryb2tlPSIjQjY3NEZGIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iNjMuNzUiIG1hc2s9InVybCgjYikiIG9wYWNpdHk9Ii4yIj48Y2lyY2xlIGN4PSIxNjEuMDc5IiBjeT0iMTYxLjA3OSIgcj0iMTI5LjIwNCIgb3BhY2l0eT0iLjIiIHRyYW5zZm9ybT0ic2NhbGUoMSAtMSkgcm90YXRlKC0xNS4xMTkgLTYyMC4yNjIgMjc3LjMwNSkiLz48cGF0aCBkPSJNMTc3LjkwNCAxNDQuNDhBMTI5LjIgMTI5LjIgMCAwIDAgMTUwLjQ2LTgxLjc1NmExMjkuMjA0IDEyOS4yMDQgMCAwIDAtOTEuMDE5LS45MjciIG9wYWNpdHk9Ii4yIi8+PHBhdGggZD0iTTIxOS44NjggOTUuMjg1YTEyOS4yMiAxMjkuMjIgMCAwIDAgNS45OTQtOTguNzA2IDEyOS4yMTEgMTI5LjIxMSAwIDAgMC02NS41NTgtNzQuMDM0IiBvcGFjaXR5PSIuMiIvPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSI1NTQuNTE2IiB4Mj0iMjE2LjUxNiIgeTE9IjI4Ljg4OCIgeTI9IjI4Ljg4OCIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>

          <div className="hidden-xs hidden-sm" style={{ position: 'absolute', right: -50, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NDQiIGhlaWdodD0iNTUwIiBmaWxsPSJub25lIj48ZyBjbGlwLXBhdGg9InVybCgjYSkiIG9wYWNpdHk9Ii40Ij48bWFzayBpZD0iYyIgd2lkdGg9Ijg5MyIgaGVpZ2h0PSI5NDIiIHg9IjEyOSIgeT0iLTIyOSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNiKSIgZD0iTTEyOS0yMjloODkzdjk0MkgxMjl6Ii8+PC9tYXNrPjxnIHN0cm9rZT0iI0I2NzRGRiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjYzLjc1IiBtYXNrPSJ1cmwoI2MpIj48Y2lyY2xlIGN4PSI1MzUuMjUiIGN5PSIzMDYuMjUiIHI9IjM2Mi45MTQiIG9wYWNpdHk9Ii4yIiB0cmFuc2Zvcm09InJvdGF0ZSgxMTguNDczIDUzNS4yNSAzMDYuMjUpIi8+PHBhdGggZD0iTTIwMy4zOTkgMTU5LjM0MmEzNjIuOTE1IDM2Mi45MTUgMCAwIDEgNjM4LjM2NS00Ny40MDMgMzYyLjkxNSAzNjIuOTE1IDAgMCAxIDUyLjA1MiAyNTAuMzE4IiBvcGFjaXR5PSIuMiIvPjxwYXRoIGQ9Ik0yNTMuNDg5IDc3LjUyQTM2Mi45MTUgMzYyLjkxNSAwIDAgMSA3NjMuOTggMjQuNDkiIG9wYWNpdHk9Ii4yIi8+PC9nPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImIiIHgxPSIxMjkiIHgyPSIzMjIiIHkxPSIyNDIiIHkyPSIzMjYuNSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PGNsaXBQYXRoIGlkPSJhIj48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMC0yMjloNTQ0djc3OUgweiIvPjwvY2xpcFBhdGg+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>
        </Container>
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 60 }}
        dashedBorder
        examName="TOEFL"
        navigationItems={props.navigationItems}
        navigationStyle={{ background: 'transparent' }}
        notice={props.notice}
      >
        <div style={{ zIndex: 1 }}>
          <HeroSection
            header={<>The smartest way to master the TOEFL<span className={styles.registeredMark}>®</span></>}
            interactivePrompt={{
              answerChoices: (
                <div className={cx(toeflStyles.answerChoicesContainer, 'align-items-center d-flex flex-1')}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" fill="none"><path fill="#5C5C5C" fill-opacity=".15" d="M14 28c4.412 0 8-3.59 8-8V8c0-4.412-3.588-8-8-8S6 3.588 6 8v12c0 4.41 3.588 8 8 8Zm14-8v-4a2 2 0 0 0-4 0v4c0 5.514-4.486 10-10 10S4 25.514 4 20v-4a2 2 0 0 0-4 0v4c0 7.04 5.226 12.864 12 13.84V36H6a2 2 0 1 0 0 4h16a2 2 0 0 0 0-4h-6v-2.16c6.774-.976 12-6.8 12-13.84Z"/></svg>
                </div>
              ),
              prompt: "You are working at an electronics store. Your manager is teaching you how to help customers set up their home Wi-Fi networks. Listen to the manager and repeat what the manager says. Repeat only once.",
              subject: 'Speaking'
            }}
            mapInCents={props.mapInCents}
            subCTA={
              <>
                <div className="align-items-center d-flex gap-s hidden-xs text-inter">
                  <div className="flex-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="38" fill="none"><path fill="#B674FF" d="M36.659 6.984 35.12 4.927l.837-2.43A1.095 1.095 0 0 0 34.58 1.13l-2.45.82L30.07.414a1.094 1.094 0 0 0-1.723.881l.049 2.572L26.3 5.343a1.095 1.095 0 0 0 .301 1.915l2.451.76.76 2.451a1.095 1.095 0 0 0 1.916.307l1.493-2.096 2.583.033a1.095 1.095 0 0 0 .864-1.729zm-3.984-.47a1.1 1.1 0 0 0-.88.454l-.548.771-.284-.902a1.1 1.1 0 0 0-.711-.712l-.903-.279.771-.547a1.1 1.1 0 0 0 .444-.886v-.947l.755.547a1.1 1.1 0 0 0 .995.154l.908-.301-.306.897a1.1 1.1 0 0 0 .159.99l.547.76zM20.776 16.368a1.094 1.094 0 0 0 1.532 0l4.58-4.585a1.094 1.094 0 0 0-1.527-1.527l-4.585 4.585a1.094 1.094 0 0 0 0 1.527M14.117 11.077a1.094 1.094 0 0 0 1.094-1.095V1.283a1.094 1.094 0 1 0-2.161 0v8.71a1.094 1.094 0 0 0 1.094 1.095zM36.838 25.379a1.095 1.095 0 0 0-.788-1.308l-8.71-2.15a1.093 1.093 0 1 0-.547 2.095l8.71 2.162a1.094 1.094 0 0 0 1.308-.788zM25.682 26.517 10.554 11.394a1.095 1.095 0 0 0-1.762.35L.235 32.612A3.2 3.2 0 0 0 0 33.832a3.24 3.24 0 0 0 4.465 3.004l20.862-8.558a1.093 1.093 0 0 0 .355-1.761M10.177 14.069l3.49 3.475H8.743zm-2.32 5.663h7.972l4.322 4.323H6.09zm-4.18 15.101a1.094 1.094 0 0 1-1.428-1.417l.306-.744h6.341zm10.516-4.311H3.43l1.772-4.323h17.104l.7.695zM28.156 16.477a1.095 1.095 0 1 1-2.189 0 1.095 1.095 0 0 1 2.19 0M19.544 12.155a1.094 1.094 0 1 1-2.189 0 1.094 1.094 0 0 1 2.189 0M35.793 15.4H34.7v-1.095a1.094 1.094 0 0 0-2.188 0v1.094h-1.095a1.094 1.094 0 0 0 0 2.189h1.095v1.094a1.095 1.095 0 0 0 2.188 0v-1.138h1.094a1.094 1.094 0 0 0 0-2.189zM18.45 4.522h1.094v1.094a1.094 1.094 0 1 0 2.161 0V4.522H22.8a1.094 1.094 0 1 0 0-2.161h-1.095V1.267a1.094 1.094 0 1 0-2.16 0V2.36H18.45a1.095 1.095 0 0 0 0 2.161"/></svg>
                  </div>

                  <div>
                    <strong>Bonus! Save on your TOEFL registration fee:</strong> $40 off in the US, and up to 10% off internationally. See FAQ for details.
                  </div>
                </div>

                <div className="small text-inter visible-xs">
                  <strong>🎉 Bonus! Save on your TOEFL registration fee:</strong> $40 off in the US, and up to 10% off internationally. See FAQ for details.
                </div>
              </>
            }
            subHeader={
              <>
                Self-paced course built
                {' '}<UnderlinedText color="#B674FF">from authentic TOEFL® questions</UnderlinedText>
                {' '}created by ETS, an AI tutor, video lessons, and top-rated mobile apps.
              </>
            }
          />
        </div>

        <SocialProofSection
          quotes={[
            {
              content: <>"The questions are very realistic and the solutions <mark style={{ background: '#FCF0BF' }}>clear every possible doubt</mark>. The graded essays were very insightful and helped me to improve. The <mark style={{ background: '#FCF0BF' }}>amount of practice is more than enough</mark> to exercise."</>,
              scores: [{ name: 'TOEFL Score', score: '5' }],
              studentName: 'Martin C.'
            },
            {
              content: <>"Magoosh not only <mark style={{ background: '#FCF0BF' }}>elevated my score</mark> but ensured I was familiar with the test structure. The detailed breakdown of each section, coupled with <mark style={{ background: '#FCF0BF' }}>realistic practice tests</mark>, equipped me to navigate the TOEFL seamlessly."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Pratheek  B.'
            },
            {
              content: <>"<mark style={{ background: '#FCF0BF' }}>All the videos</mark> transformed the way I see the exam. I started answering the questions using the structure for each section, and <mark style={{ background: '#FCF0BF' }}>my score improved considerably</mark> (+21!). On TOEFL day, I knew exactly what to do."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Carol S.'
            },
            {
              content: <>"The <mark style={{ background: '#FCF0BF' }}>detailed insight</mark> into my potential areas of improvement based on my performance in the practice tests <mark style={{ background: '#FCF0BF' }}>boosted my confidence</mark> on the day of the test. It was a cakewalk, thanks to Magoosh."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Adithi K.'
            }
          ]}
          tagline="Purchased by 75,000+ TOEFL students"
        />

        <div className="d-flex flex-column gap-m">
          <CompetitorComparisonSection
            competitorShortfalls={[
              'Many rely on simulated or third-party questions',
              'No AI support to guide your studying',
              'Text-based explanations only',
              'Not optimized for mobile',
              <>Costs upwards of <strong>$249</strong></>
            ]}
            magooshFeatures={[
              'Features authentic TOEFL® questions created by ETS and used under license',
              'Official TOEFL® writing and speaking grading by ETS',
              'Get a Magoosh-trained AI tutor that works with you until you get it',
              'Video and text-based lessons to support multiple learning styles',
              'Download our mobile apps to practice on the go',
              <>Starts at an affordable <strong>{formatPrice(props.mapInCents, 'usd', true)}</strong></>
            ]}
            pointIncrease={4}
            title="Better prep. Better scores. Guaranteed."
           />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <OfficialQuestionsSection />

        <ValuePropsSection />

        <AISection />

        <AppsSection />

        <VideoBasedLearningSection />

        <WallOfLoveSection />

        <FeaturesSection />

        <div className="d-flex flex-column gap-m">
          <GuaranteeSection pointIncrease={4} />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Yes! The <a href="/toefl-2026-changes">TOEFL changed on January 21, 2026</a>, and our content is fully updated for the new test format. All Magoosh lessons are aligned with the 2026 TOEFL, and our self-paced course includes over 1,300 TOEFL® practice questions used under license from ETS, the makers of the test, so you know you're studying exactly what you'll see on test day.</p>
                </>
              ),
              question: 'Can I use Magoosh to study for the new 2026 TOEFL?'
            },
            {
              answer: <>
                <p>When you sign up for Magoosh Premium, we'll email you a voucher code you can use to save on your TOEFL iBT registration fee at ETS. The discount depends on where you're registering for the test:</p>
                
                <ul>
                  <li><strong>United States</strong>: Save $40 on registration</li>
                  <li><strong>India</strong>: Save 3% on registration</li>
                  <li><strong>All other countries</strong>: Save 10% on registration*</li>
                </ul>

                <p>*In China, the voucher is valid for the TOEFL iBT Home Edition only. In all other countries, the voucher applies to both the Home Edition and test center registration. To redeem it, simply enter the voucher code during checkout on the ETS website when you register for your test. The voucher is valid for TOEFL iBT registration only and cannot be combined with other ETS promotions. Please check your email after purchasing your Magoosh plan — the voucher and instructions will be sent to the same email address you used to sign up.</p>
              </>,
              question: 'How does the TOEFL registration discount voucher work?'
            },
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video or text explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Yes! Check out our <a href="https://ielts.magoosh.com">IELTS website</a> to learn more.
                </>
              ),
              question: 'Does Magoosh also offer IELTS prep?'
            }
          ]}
          footerNote={<>A full list of frequently asked questions and their answers can be found on the <a className="text-body" href="/plans#faq">plans page</a>.</>}
        />
      </PageLayout>
    </div>
  );
};
